/**
 * Loading Interceptor
 *
 * Functional HTTP interceptor that tracks pending HTTP requests and updates
 * the global loading state accordingly. Shows a loading indicator when requests
 * are in progress and hides it when all requests complete.
 *
 * This interceptor follows the Angular 15+ functional interceptor pattern.
 *
 * Features:
 * - Tracks the number of pending HTTP requests
 * - Shows loading indicator when requests are active
 * - Hides loading indicator when all requests complete
 * - Handles request errors and cancellations properly
 *
 * @example
 * ```typescript
 * // In app.config.ts
 * provideHttpClient(
 *   withInterceptors([authInterceptor, errorInterceptor, loadingInterceptor])
 * )
 *
 * // In a component template
 * <p-progressSpinner *ngIf="loadingService.loading$ | async"></p-progressSpinner>
 * ```
 */

import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { LoadingService } from '../services/loading.service';

/**
 * Counter to track the number of pending HTTP requests
 * Incremented when a request starts, decremented when it completes
 */
let pendingRequestsCount = 0;

/**
 * Intercepts HTTP requests to manage global loading state
 *
 * Increments the pending request counter when a request starts,
 * and decrements it when the request completes (success or error).
 * Updates the LoadingService to show/hide the loading indicator.
 *
 * @param req - The outgoing HTTP request
 * @param next - The next handler in the interceptor chain
 * @returns Observable of the HTTP response
 *
 * @example
 * When a request starts:
 * - pendingRequestsCount increases from 0 to 1
 * - LoadingService.show() is called
 * - Loading spinner appears
 *
 * When the request completes:
 * - pendingRequestsCount decreases from 1 to 0
 * - LoadingService.hide() is called
 * - Loading spinner disappears
 */
export const loadingInterceptor: HttpInterceptorFn = (req, next) => {
  const loadingService = inject(LoadingService);

  // Increment pending requests counter
  pendingRequestsCount++;

  // Show loading indicator if this is the first pending request
  if (pendingRequestsCount === 1) {
    loadingService.show();
  }

  return next(req).pipe(
    finalize(() => {
      // Decrement pending requests counter
      pendingRequestsCount--;

      // Hide loading indicator when all requests complete
      if (pendingRequestsCount === 0) {
        loadingService.hide();
      }

      // Ensure counter never goes negative (defensive programming)
      if (pendingRequestsCount < 0) {
        pendingRequestsCount = 0;
      }
    })
  );
};
